import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { useReducedMotion } from 'framer-motion';
import { formatInt } from '../utils/format';
interface LiveCounterProps {
  base: number;
  baseTime: number;
  ratePerSec: number;
  label: string;
  className?: string;
  tailClassName?: string;
}
const TWEEN_MS = 280;
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
export function LiveCounter({
  base,
  baseTime,
  ratePerSec,
  label,
  className = '',
  tailClassName = 'opacity-50'
}: LiveCounterProps) {
  const headRef = useRef<HTMLSpanElement>(null);
  const tailRef = useRef<HTMLSpanElement>(null);
  const shownRef = useRef<number | null>(null);
  const tweenRef = useRef<{
    from: number;
    start: number;
  } | null>(null);
  const reduce = useReducedMotion();
  useLayoutEffect(() => {
    if (shownRef.current !== null && !reduce) tweenRef.current = {
      from: shownRef.current,
      start: performance.now()
    };
  }, [base, ratePerSec, baseTime, reduce]);
  useEffect(() => {
    const targetAt = (now: number) => base + ratePerSec * Math.max(0, now - baseTime) / 1000;
    let raf = 0;
    let interval = 0;
    const paint = () => {
      let v = targetAt(Date.now());
      const tw = tweenRef.current;
      if (tw) {
        const p = Math.min(1, (performance.now() - tw.start) / TWEEN_MS);
        v = tw.from + (v - tw.from) * easeOut(p);
        if (p >= 1) tweenRef.current = null;
      }
      shownRef.current = v;
      const s = formatInt(v);
      const head = s.length > 3 ? s.slice(0, -3) : '';
      const tail = s.length > 3 ? s.slice(-3) : s;
      if (headRef.current && headRef.current.textContent !== head) headRef.current.textContent = head;
      if (tailRef.current && tailRef.current.textContent !== tail) tailRef.current.textContent = tail;
    };
    paint();
    if (reduce) {
      interval = window.setInterval(paint, 1000);
    } else {
      const loop = () => {
        paint();
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
    }
    return () => {
      cancelAnimationFrame(raf);
      window.clearInterval(interval);
    };
  }, [base, ratePerSec, baseTime, reduce]);
  const approx = base + ratePerSec * Math.max(0, Date.now() - baseTime) / 1000;
  return <>
      <span className={`font-mono tabular whitespace-nowrap ${className}`} aria-hidden="true" data-id="element-278" data-mp="2arn4zcwcjuip">
        <span ref={headRef} data-id="element-279" data-mp="25sk0btka3qb9" />
        <span ref={tailRef} className={tailClassName} data-id="element-280" data-mp="1i4snmouttb9g" />
      </span>
      <span className="sr-only" data-id="element-281" data-mp="1i2y1j77tjd3s">
        {label}: approximately {formatInt(approx)}
      </span>
    </>;
}